<template>
  <div class="canvas-scroll">
    <v-layout row align-center ml-4 wrap v-if="tabCount > 1">
      <v-flex md4 lg3 class="selectRoomDropdown">
        <div>
          <v-select
            v-model="selection"
            :items="tabArray"
            label="Select Side"
            outline
          ></v-select>
        </div>
      </v-flex>
      <v-flex md4 lg6>
        <div class="live-container" style="margin: 0 auto;">
          <div class="live-data">{{ $t("LIVE") }}</div>
          <v-progress-linear
            style="width: 50px"
            :indeterminate="true"
            height="3"
            color="accent"
          ></v-progress-linear>
        </div>
      </v-flex>
    </v-layout>
    <v-container
      fluid
      grid-list-md
      pa-0
      class="live-bg"
      v-else
    >
      <v-layout row align-center justify-center wrap>
        <v-spacer></v-spacer>
        <v-flex xs1>
          <div class="live-container">
            <div class="live-data">{{ $t("LIVE") }}</div>
            <v-progress-linear
              :indeterminate="true"
              height="3"
              color="accent"
            ></v-progress-linear>
          </div>
        </v-flex>
        <v-spacer></v-spacer>
      </v-layout>
    </v-container>
    <v-layout id="tdHover" column>
      <div id="ioTable"></div>
    </v-layout>
    <div id="drawCanvas"></div>
    <div class="legend">
      <div class="normal">
        <span :class="iconClass.success"></span> {{ $t("Closed / On") }}
      </div>
      <div class="fault">
        <span :class="iconClass.failure"></span> {{ $t("Open / Off / Trip") }}
      </div>
      <div class="acknowledge">
        <span class="circle-grey"></span> {{ $t("No Data") }}
      </div>
    </div>
  </div>
</template>

<script>
const moment = require("moment");
import _ from "lodash";
import { mapGetters, mapMutations } from "vuex";

import { mapGettersMixin } from "@/util/mixin/index";
import { WriteTableRow, WriteAnnTableRow } from "@/components/sld/js/DrawTable";
import sldAttr from "@/components/sld/json/attributes";
import SldData from "@/services/SLD";

export default {
  mixins: [mapGettersMixin],
  data() {
    return {
      customCallBack: null,
      paper: null,
      tabCount: null,
      selection: "",
      imageMap: {},
      sldSym: {},
      hoverData: {},
      svgImage: {},
      tableDataNode: {},
      isMounted: true,
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getActivePlantInfo"]),
    computedSvgImage() {
      const baseUrl = process.env.VUE_APP_BASE_URL || ("http://" + location.hostname + ":3030");
      let svgObj = {};
      
      Object.keys(this.svgImage).forEach((key) => {
        let logo = this.svgImage[key];

        if (!this.isDark) {
          logo = logo.replace("sld_v1.1/", "sld_v1.1_light/");
        }

        const encodedUrl = encodeURIComponent(logo);
        svgObj[key] = `${baseUrl}/api/v2/assets?url=${encodedUrl}`
      });
      
      return svgObj;
    },
    deviceIconMap() {
      if (_.isEmpty(this.computedSvgImage)) {
        return {};
      } else {
        return {
          inverter: this.computedSvgImage["invImageOff"],
          invImageOn: this.computedSvgImage["invImageOff"],
          invImageOn1: this.computedSvgImage["invImageOff1"],
        };
      }
    },
    tabArray() {
      return this.sldSym.tabs;
    },
    iconColor() {
      if (
        this.sldSym.iconStatusColor &&
        this.sldSym.iconStatusColor.failure === "green"
      ) {
        return {
          success: "red",
          failure: "green",
        };
      } else {
        return {
          success: "green",
          failure: "red",
        };
      }
    },
    iconClass() {
      return {
        success:
          this.iconColor.success == "green" ? "circle-green" : "circle-red",
        failure:
          this.iconColor.failure == "green" ? "circle-green" : "circle-red",
      };
    },
    availableDeviceTypes() {
      if (
        this.sldSym[this.selection] &&
        (this.sldSym[this.selection].devices ||
          this.sldSym[this.selection].image)
      ) {
        return [
          ...new Set([
            ...this.sldSym[this.selection].image
              .filter((el) => el.device && el.device != "" && el.uId != "")
              .map((el) => el.device),
            ...this.sldSym[this.selection].devices,
          ]),
        ];
      }

      return [];
    },
    tableParameterAvailable() {
      if (
        this.sldSym[this.selection] &&
        (this.sldSym[this.selection].devices ||
          this.sldSym[this.selection].image)
      ) {
        return _.fromPairs(
          this.availableDeviceTypes.map((d) => {
            return [
              d,
              this.getActivePlantInfo([
                "rule",
                "comp_config",
                "sld",
                "options",
                "table",
              ])[d],
            ];
          })
        );
      }
      return {};
    },
    colorCode() {
      return {
        null: "#9E9E9E",
        "No Data": "#9E9E9E",
        0: this.iconColor['failure'] == "green" ? "#3CB371" : "#FF5252",
        1: this.iconColor['success'] == "green" ? "#3CB371" : "#FF5252",
      };
    },
  },
  async mounted() {
    try {
      this.SET_PROGRESS_BAR(true);
      this.svgImage = await SldData.getSVGimage("sld");
      this.sldSym = await SldData.getSymbol(this.getSelectedPlantId);
      this.SET_PROGRESS_BAR(false);
      this.selection = this.sldSym.tabs[0];
      this.tabCount = this.sldSym.tabs.length;
    } catch (err) {
      this.SET_PROGRESS_BAR(false);
    }

    this.drawSld();
  },
  destroyed() {
    if (this.paper) {
      this.paper.clear();
    }
  },
  methods: {
    ...mapMutations(["SET_LAST_CALL_TIME", "SET_PROGRESS_BAR"]),
    callAllApi() {
      let updateTime = moment().format("MM-DD-YYYY H:mm:ss");
      this.SET_LAST_CALL_TIME(updateTime);

      Promise.all(
        this.availableDeviceTypes.map((D) => {
          return this.loadData(D);
        })
      ).finally((el) => {
        this.isMounted = false;
      });
    },
    async loadData(device) {
      const table = this.sldSym[this.selection].table[device];
      const imageList = this.getImageList(device);
      const tHeight = 20;
      
      if (!this.tableParameterAvailable[device]) return;

      const param = btoa(JSON.stringify(this.tableParameterAvailable[device]));
      const message = await SldData.getDataV2(
        this.getSelectedPlantId,
        device,
        param
      );

      const tableArr = Array.isArray(table) ? table : [table];
      this.updateImageIcons(device, imageList, message);

      tableArr.forEach((tableConfig) => {
        const {
          header = [],
          paramMap = {},
          data = {},
          name = "",
          direction,
          threshold = {},
        } = tableConfig;
        const dataHeaderKey =
          Object.keys(data).length > 0 ? Object.keys(data)[0] : "";
        const headerExist = !(
          name === "" &&
          dataHeaderKey &&
          data[dataHeaderKey] &&
          data[dataHeaderKey].name === ""
        );

        if (this.isMounted)
          this.renderHeaders(header, paramMap, headerExist, name, tHeight);

        this.renderTableData(
          device,
          data,
          paramMap,
          message,
          headerExist,
          tHeight,
          threshold,
          direction
        );
      });
    },
    getImageList(device) {
      if (device === "inverter" || device === "breaker") {
        return this.sldSym[this.selection].image.filter(
          (obj) => obj.device === device
        );
      }
      return [];
    },
    renderHeaders(header, paramMap, headerExist, headerName, tHeight) {
      header.forEach(({ x, y, width: cWidth }) => {
        if (headerExist) {
          WriteTableRow(
            "#FF7247",
            x,
            y,
            cWidth,
            tHeight,
            this.paper,
            headerName,
            this.isDark,
            this.isMounted
          );
        }

        Object.values(paramMap).forEach((name) => {
          y = y + tHeight;
          WriteTableRow(
            this.isDark ? "#FFFFFF" : "#04112F",
            x,
            y,
            cWidth,
            tHeight,
            this.paper,
            name,
            this.isDark,
            this.isMounted
          );
        });
      });
    },
    updateImageIcons(device, imageList, message, isHover = false) {
      imageList
        .filter(
          (el) =>
            el.uId &&
            (isHover
              ? device === el.device && el.uId === this.hoverData.id
              : true)
        )
        .forEach((el) => {
          let idKey = el.uId;
          let jusVal = message[idKey];

          if (
            !(this.imageMap[device][idKey] && this.imageMap[device][idKey].obj)
          )
            return;

          if (device === "inverter") {
            this.updateInverterIcon(el, jusVal, idKey);
          } else if (device === "breaker") {
            this.updateBreakerIcon(
              el,
              isHover
                ? {
                    ON: jusVal.breaker_on,
                    OFF: jusVal.breaker_off,
                    TRIP: jusVal.breaker_trip,
                  }
                : jusVal,
              idKey
            );
          } else if (device.includes("annunciator")) {
            this.updateAnnunciatorIcon(device, jusVal, idKey);
          }

          if (el.deg > 0) {
            this.imageMap[device][idKey].obj.rotate(el.deg);
          }
        });
    },
    renderTableData(
      device,
      tableData,
      paramMap,
      message,
      headerExist,
      tHeight,
      threshold,
      direction
    ) {
      if (_.isEmpty(paramMap)) return;

      if (!this.tableDataNode[device]) this.tableDataNode[device] = {};

      Object.keys(tableData).forEach((idKey) => {
        const jusVal = message[idKey];
        if (!this.tableDataNode[device][idKey])
          this.tableDataNode[device][idKey] = [];

        let { x, y, width: cWidth, name } = tableData[idKey];

        if (headerExist && this.isMounted) {
          WriteTableRow(
            this.isDark ? "#FFFFFF" : "#04112F",
            x,
            y,
            cWidth,
            tHeight,
            this.paper,
            name,
            this.isDark,
            this.isMounted
          );
        }

        Object.keys(paramMap).forEach((key) => {
          if (!direction || direction === "default") {
            y += tHeight;
          } else {
            x += cWidth;
          }
          let dataValue = this.getDataValue(device, key, jusVal);
          let color = this.getColorForValue(device, key, dataValue, threshold);
          let text =
            device.includes("annunciator") || device == "relay"
              ? WriteAnnTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  "",
                  this.isDark,
                  this.isMounted
                )
              : WriteTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  dataValue,
                  this.isDark,
                  this.isMounted
                );
          this.tableDataNode[device][idKey].push(text);
        });
      });
    },
    getDataValue(device, key, jusVal) {
      if (!jusVal || jusVal[key] === undefined) return "No Data";
      const value = parseFloat(jusVal[key]);
      if (isNaN(value)) return "No Data";

      if (device.includes("annunciator")) {
        return value.toFixed(0);
      }

      return value.toFixed(1);
    },
    updateInverterIcon(el, jusVal, idKey) {
      const defaultSuccessIcon = this.computedSvgImage[el.id];
      const defaultFailureIcon = this.deviceIconMap[el.id];
      

      const [successIcon, failureIcon] = this.iconColor.success === "red" 
        ? [defaultFailureIcon, defaultSuccessIcon]
        : [defaultSuccessIcon, defaultFailureIcon];


      const inverterObj = this.imageMap.inverter[idKey] && this.imageMap.inverter[idKey].obj ? this.imageMap.inverter[idKey].obj : null;
      
      if (!inverterObj) return;
      
      const iconToDisplay = !jusVal 
        ? failureIcon 
        : (jusVal["Inverter Alert"] === 1 || jusVal["dc_power"] <= 0 ? failureIcon : successIcon);
      
      inverterObj.attr({ src: iconToDisplay });
    },
    updateBreakerIcon(el, jusVal, idKey) {
      let val = 2;

      if (jusVal && (jusVal["ON"] !== undefined || jusVal["OFF"] !== undefined || jusVal["TRIP"] !== undefined)) {
        val = jusVal["ON"] === 1 ? 1 : (jusVal["OFF"] === 1 || jusVal["TRIP"] === 1 ? 0 : 2);
      }
      let iconMap = this.getBreakerIcons(el.id);

      this.imageMap.breaker[idKey].obj.attr({ src: iconMap[val] });
    },
    getBreakerIcons(id) {
      if (!id || !["vcb", "isolatorVer", "motorizedSwitchVer", "motorizedSwitchHor", 
                  "earthSwitchVer", "earthSwitchHor", "isolator", "rectangle"].includes(id)) {
        return ["", "", ""];
      }

      const isSuccessIconRed = this.iconColor.success === "red";
      const baseUrlLogo = "https://scada4x-assets.s3.ap-south-1.amazonaws.com/sld_v1.1/";
      
      const createPaths = (folder, base, activeRed, inactive) => {
        const activePath = isSuccessIconRed ? `${folder}/${base}.svg` : `${folder}/${activeRed}`;
        const inactivePath = isSuccessIconRed ? `${folder}/${activeRed}` : `${folder}/${base}.svg`;
        return [`${baseUrlLogo}${activePath}`, `${baseUrlLogo}${inactivePath}`, `${baseUrlLogo}${inactive}`];
      };

      const iconConfig = {
        vcb: createPaths("vcb", "sld_vcb_green_ta1_ba1", "sld_vcb_red_ta1_ba1.svg", `vcb/sld_vcb_ta1_ba1_disable.svg`),
        isolatorVer: [`${baseUrlLogo}isolator/isolatorVer.svg`, `${baseUrlLogo}isolator/isolatorVerRed.svg`, `${baseUrlLogo}isolator/isolatorVer.svg`],
        motorizedSwitchVer: [`${baseUrlLogo}isolator/motorizedSwitchVer.svg`, `${baseUrlLogo}isolator/motorizedSwitchRedVer.svg`, `${baseUrlLogo}isolator/motorizedSwitchVer.svg`],
        motorizedSwitchHor: [`${baseUrlLogo}isolator/motorizedSwitchHor.svg`, `${baseUrlLogo}isolator/motorizedSwitchRedHor.svg`, `${baseUrlLogo}isolator/motorizedSwitchHor.svg`],
        earthSwitchVer: [`${baseUrlLogo}isolator/earthSwitchVer.svg`, `${baseUrlLogo}isolator/earthSwitchRedVer.svg`, `${baseUrlLogo}isolator/earthSwitchVer.svg`],
        earthSwitchHor: [`${baseUrlLogo}isolator/earthSwitchHor.svg`, `${baseUrlLogo}isolator/earthSwitchRedHor.svg`, `${baseUrlLogo}isolator/earthSwitchHor.svg`],
        isolator: createPaths("isolator", "isolator", "isolatorRed.svg", `isolator/isolatorDisable.svg`),
        rectangle: createPaths("scb", "rectangleGreen", "rectangleRed.svg", `scb/rectangleDisable.svg`)
      };

      const baseUrl = process.env.VUE_APP_BASE_URL || ("http://" + location.hostname + ":3030");

      return iconConfig[id].map(path => {
        const encodedUrl = encodeURIComponent(path);
        return `${baseUrl}/api/v2/assets?url=${encodedUrl}`;
      });
    },
    updateAnnunciatorIcon(device, jusVal, idKey) {
      let computedData = Object.values(
        this.tableParameterAvailable[device] || {}
      ).reduce((sum, tag) => sum + ((jusVal && jusVal[tag]) || 0), 0);
      let icon =
        computedData > 0
          ? this.computedSvgImage.annunciatorRed
          : computedData === 0
          ? this.computedSvgImage.annunciator
          : this.computedSvgImage.annunciatorGrey;
      this.imageMap[device][idKey].obj.attr({ src: icon });
    },
    getColorForValue(device, key, dataValue, threshold) {
      const RED = "#FF5252";
      const BLUE = "#668cf6";
      
      if (dataValue === "No Data") {
        return device.includes("annunciator") || device.includes("relay") 
          ? this.colorCode['No Data'] 
          : RED;
      }
      
      if (device.includes("annunciator") || device.includes("relay")) {
        return parseFloat(dataValue) > 0 ? this.colorCode[1] : this.colorCode[0];
      }
      
      return parseFloat(dataValue) > (threshold[key] || 0) ? BLUE : RED;
    },  
    updateTableData() {
      let device = this.hoverData.device;
      let idKey = this.hoverData.id || "";
      let hoverData = this.hoverData.data || {};
      const table = this.sldSym[this.selection].table[device];
      if (!table) return;
      if (idKey.length === 0) return;

      const imageList = this.getImageList(device);
      const tHeight = 20;
      const tableArr = Array.isArray(table) ? table : [table];

      this.updateImageIcons(
        device,
        imageList,
        { [idKey]: hoverData },
        true
      );

      if (this.tableDataNode[device] && this.tableDataNode[device][idKey]) {
        this.tableDataNode[device][idKey].forEach((el) => el.remove());
      }

      let newVal = {};
      const tableParameters = this.tableParameterAvailable[device] || {};
      let paramTable = Object.entries(tableParameters);
      if (device === "inverter")
        paramTable.push(["Generation (kWh)", "Generation (kWh)"]);
      if (device === "energy meter")
        paramTable.push(
          ["Daily Export (kWh)", "Daily Export (kWh)"],
          ["Daily Import (kWh)", "Daily Import (kWh)"]
        );
      paramTable.forEach(([p1, p2]) => {
        newVal[p2] = device === "zmu" ? hoverData[p2] : hoverData[p1];
      });

      tableArr.forEach((tableConfig) => {
        const {
          paramMap = {},
          data = {},
          direction,
          threshold = {},
        } = tableConfig;

        if (_.isEmpty(paramMap)) return;
        if (!Object.keys(data).length) return;

        let { x, y, width: cWidth } = data[idKey] || {};
        Object.keys(paramMap).forEach((key) => {
          if (!direction || direction === "default") {
            y += tHeight;
          } else {
            x += cWidth;
          }
          let dataValue = this.getDataValue(device, key, newVal);
          let color = this.getColorForValue(device, key, dataValue, threshold);
          let text =
            device.includes("annunciator") || device == "relay"
              ? WriteAnnTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  dataValue,
                  this.isDark,
                  this.isMounted
                )
              : WriteTableRow(
                  color,
                  x,
                  y,
                  cWidth,
                  tHeight,
                  this.paper,
                  dataValue,
                  this.isDark,
                  this.isMounted
                );
          this.tableDataNode[device][idKey].push(text);
        });
      });
    },
    setToFixed(value, valueID, precession = 2) {
      if (value === null || value === undefined) {
        return value;
      } else if (valueID === undefined) {
        return value;
      } else {
        if (typeof value[valueID] === "number") {
          return parseFloat(value[valueID]).toFixed(precession);
        } else {
          return value[valueID];
        }
      }
    },
    tablePosition: (event) => {
      return event.clientX > screen.width - 550;
    },
    displayTable: async function (device, uId, hoverConfig, tableIsLeft) {
      if (!device || !uId) return;

      const tableResponse = await SldData.geHoverDataV2(
        this.getSelectedPlantId,
        device,
        uId
      );

      const requiredResponse = this.getRequiredResponse(
        device,
        tableResponse[uId]
      );
      this.updateHoverTableData(device, uId, requiredResponse);
      this.updateHoverTableLayout(tableIsLeft);
      this.renderHoverTable(device, hoverConfig, tableResponse);
    },
    getRequiredResponse(device, response) {
      let requiredResponse = {};
      if (device === "inverter") {
        requiredResponse["Generation (kWh)"] = response["Generation (kWh)"];
        response["alert"] = response["ac_power"] > 0 ? 0 : 1;
      } else if (device === "energy meter") {
        requiredResponse["Daily Export (kWh)"] = response["Daily Export (kWh)"];
        requiredResponse["Daily Import (kWh)"] = response["Daily Import (kWh)"];
      }

      const tableParameters = this.tableParameterAvailable[device] || {};
      Object.keys(tableParameters).forEach((el) => {
        requiredResponse[el] = response && response[el] != null ? response[el] : null;
      });
      return requiredResponse;
    },
    updateHoverTableData(device, id, requiredResponse) {
      this.hoverData = {
        device,
        id,
        data: requiredResponse,
      };

      this.updateTableData();
    },
    updateHoverTableLayout(tableIsLeft) {
      let tableDateContainer = document.getElementById("tdHover");
      tableDateContainer.classList.toggle("rightData", !tableIsLeft);
      tableDateContainer.classList.toggle("leftData", tableIsLeft);
    },
    renderHoverTable(device, hoverConfig, tableResponse) {
      const tableDate = document.getElementById("ioTable");
      const emValue = Object.values(tableResponse)[0];
      let { input: symInput, output: symOutput } = hoverConfig;
      let inputKeys = Object.keys(symInput);
      let outputKeys = Object.keys(symOutput);

      let tempTable = `<table id="hoverTable"><tr id="tableRow">`;
      if (inputKeys.length)
        tempTable += `<th class="col">${this.$t(
          "Input"
        )}</th><th class="col">${this.$t("Value")}</th>`;
      if (outputKeys.length)
        tempTable += `<th class="col">${this.$t(
          "Output"
        )}</th><th class="col">${this.$t("Value")}</th>`;
      tempTable += `</tr>`;

      let range = Math.max(inputKeys.length, outputKeys.length);
      for (let index = 0; index < range; index++) {
        let inputIndex = this.setToFixed(
          emValue,
          inputKeys[index],
          device.includes("annunciator") ? 0 : undefined
        );
        let outputIndex = this.setToFixed(
          emValue,
          outputKeys[index],
          device.includes("annunciator") ? 0 : undefined
        );

        tempTable += `<tr id="tableRow">`;
        tempTable += this.renderHoverTableRow(
          inputKeys,
          index,
          symInput,
          inputIndex
        );
        tempTable += this.renderHoverTableRow(
          outputKeys,
          index,
          symOutput,
          outputIndex
        );
        tempTable += `</tr>`;
      }
      tempTable += `</table>`;
      tableDate.innerHTML = tempTable;
    },
    renderHoverTableRow(keys, index, symMap, value) {
      if (!keys.length) return "";
      let hovData = this.getHoverData(value, index, keys.length);
      let className =
        hovData === "No Data" || hovData == null ? "col-no-data" : "col-value";
      return `<td id="tableData" class="col">${
        index < keys.length ? symMap[keys[index]] : ""
      }</td><td id="tableData" class="${className} value-width-col">${hovData}</td>`;
    },
    hideTable() {
      document.getElementById("tdHover").style.display = "none";
      document.getElementById("ioTable").replaceChildren();
    },
    getHoverData(outputIndex, index, totalInputKey) {
      let result = "";

      if (index < totalInputKey) {
        if (outputIndex == null) {
          result = "No Data";
        } else {
          result = outputIndex;
        }
      }
      return result;
    },
    drawImage() {
      this.sldSym[this.selection].image.forEach(
        ({ uId, device, id, x, y, width, height, location }) => {
          let items = this.paper.image(
            this.computedSvgImage[id],
            x,
            y,
            width,
            height
          );

          if (
            uId &&
            [
              "inverter",
              "breaker",
              "annunciator",
              "annunciator12",
              "annunciator34",
              "annunciatorOG",
            ].includes(device)
          ) {
            if (!this.imageMap[device]) this.imageMap[device] = {};
            this.imageMap[device][uId] = { obj: items, id };
          }

          if (this.sldSym[this.selection].hoverProperty[device]) {
            items.node.onmouseenter = (event) =>
              this.handleImageHover(event, device, uId);
            items.node.onmouseleave = () => this.hideTable();
          }

          if (location) {
            items.click(() => this.$router.push(location));
          }
        }
      );
    },
    handleImageHover(event, device, uId) {
      const isLeft = this.tablePosition(event);
      let tableDateContainer = document.getElementById("tdHover");
      tableDateContainer.style.display = "flex";

      let configHover = Array.isArray(this.sldSym[this.selection].table[device])
        ? this.sldSym[this.selection].table[device].filter(
            (obj) =>
              Object.keys(obj.data).some((id) => id == uId) &&
              ((obj.config_hover.input &&
                Object.keys(obj.config_hover.input).length > 0) ||
                (obj.config_hover.output &&
                  Object.keys(obj.config_hover.output).length > 0))
          )
        : [this.sldSym[this.selection].table[device]];

      if (configHover.length) {
        this.customCallBack = () =>
          this.displayTable(device, uId, configHover[0].config_hover, isLeft);
        this.displayTable(device, uId, configHover[0].config_hover, isLeft);
      }
    },
    drawReact() {
      let sldRect = this.sldSym[this.selection].rect
        ? this.sldSym[this.selection].rect
        : [];
      sldRect.forEach((items) => {
        let rect = this.paper.rect(items.x, items.y, items.width, items.height);
        if (!this.isDark && sldAttr[`${items.attr}Light`]) {
          rect.attr(sldAttr[`${items.attr}Light`]);
        } else {
          rect.attr(sldAttr[items.attr]);
        }
        rect.node.id = rect.id;
      });
    },
    drawText() {
      let sldText = this.sldSym[this.selection].text
        ? this.sldSym[this.selection].text
        : [];
      sldText.forEach((textItems) => {
        let text = this.paper.text(textItems.x, textItems.y, textItems.name);
        if (!this.isDark && sldAttr[`${textItems.attr}Light`]) {
          text.attr(sldAttr[`${textItems.attr}Light`]);
        } else {
          text.attr(sldAttr[textItems.attr]);
        }
      });
    },
    drawPath() {
      let sldPath = this.sldSym[this.selection].path
        ? this.sldSym[this.selection].path
        : [];
      sldPath.forEach((pathItems) => {
        let path = this.paper.path(pathItems.image);
        if (!this.isDark && sldAttr[`${pathItems.attr}Light`]) {
          path.attr(sldAttr[`${pathItems.attr}Light`]);
        } else {
          path.attr(sldAttr[pathItems.attr]);
        }
        path.node.id = pathItems.id;
      });
    },
    drawSld() {
      this.paper = Raphael(
        "drawCanvas",
        this.sldSym[this.selection].paper.width,
        this.sldSym[this.selection].paper.height
      );
      this.setIntervalWithoutDelay(() => {
        Object.keys(this.tableDataNode).forEach((device) => {
          Object.keys(this.tableDataNode[device]).forEach((id) => {
            this.tableDataNode[device][id].forEach((el) => el.remove());
          });
        });
        this.tableDataNode = {};
        if (typeof this.customCallBack === "function") {
          this.customCallBack();
        }

        this.callAllApi();
      }, 60000);
      this.drawText();
      this.drawReact();
      this.drawPath();
      this.drawImage();
    },
  },
  watch: {
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(() => {
          Object.keys(this.tableDataNode).forEach((device) => {
            Object.keys(this.tableDataNode[device]).forEach((id) => {
              this.tableDataNode[device][id].forEach((el) => el.remove());
            });
          });

          this.tableDataNode = {};
          if (typeof this.customCallBack === "function") {
            this.customCallBack();
          }

          this.callAllApi();
        }, 60000);
      }
    },
    theme() {
      this.isMounted = true;
      if (this.paper) {
        this.paper.clear();
      }
      this.drawSld();
    },
    selection(newVal, oldVal) {
      if (oldVal != "") {
        this.isMounted = true;
        if (this.paper) {
          this.paper.clear();
        }
        this.drawSld();
      }
    },
    async getSelectedPlantId() {
      try {
        this.isMounted = true;
        this.sldSym = await SldData.getSymbol(this.getSelectedPlantId);
        this.selection = this.sldSym.tabs[0] || "";
        this.tabCount = this.sldSym.tabs.length || null;
        if (this.paper) {
          this.paper.clear();
        }
        if (this.tabCount) {
          this.drawSld();
        }
      } catch (err) {
        if (this.paper) {
          this.paper.clear();
        }
      }
    },
  },
};
</script>

<style>
#drawCanvas {
  width: 1500px;
  overflow-x: visible;
  position: relative;
  top: 75px;
}

.v-progress-linear {
  margin: 0;
}

.live-data {
  transform-origin: center;
  animation: flipAni 16s infinite ease-in-out;
}

.canvas-scroll {
  overflow-x: auto;
  overflow-y: hidden;
}

.live-container {
  width: 50px;
  text-align: center;
}

.rightside {
  float: right;
}

div #ioTable {
  right: 8px;
  top: 110px;
}

div #tdHover {
  display: none;
  position: fixed;
  right: 8px;
  top: 110px;
  z-index: 3;
}

#hoverTable {
  font-family: "Trebuchet MS", Arial, Helvetica, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

#hoverTable #tableData {
  border: 1px solid #34495e;
  padding: 2px;
}

#hoverTable #tableRow {
  text-align: center;
  background-color: var(--v-primary-base);
  color: var(--v-primaryText-base);
}

.rightData {
  display: none;
  width: 600px;
  position: fixed;
  right: 8px;
  top: 110px;
  z-index: 3;
}

.leftData {
  display: none;
  width: 600px;
  position: fixed;
  left: 8px;
  top: 110px;
  z-index: 3;
}

.col {
  max-width: 120px;
  width: 100px;
  min-width: 100px;
  text-align: center;
  text-wrap: wrap;
  word-wrap: break-word;
  padding: 6px;
  border: 1px solid #34495e;
}

.col-value {
  color: #668cf6;
}

.col-no-data {
  color: #ff5252;
}

.value-width-col {
  max-width: 160px;
  width: 100px;
  min-width: 100px;
  text-align: center;
  text-wrap: wrap;
  word-wrap: break-word;
  padding: 6px;
  border: 1px solid #34495e;
}

.legend {
  position: absolute;
  top: 15px;
  right: 35px;
  width: 140px;
  color: var(--v-primaryText-base);
  border: 1px solid var(--v-primaryText-base);
  padding: 10px;
}

.circle-green {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #3cb371;
  border-radius: 50%;
}

.circle-red {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #ff5252;
  border-radius: 50%;
}

.circle-grey {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #9e9e9e;
  border-radius: 50%;
}

@keyframes flipAni {
  0% {
    transform: rotateY(0deg);
  }
  50% {
    transform: rotateY(180deg);
  }
  100% {
    transform: rotateY(360deg);
  }
}
</style>
